import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Upload, X, AlertCircle, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { authenticatedFetch } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { 
  mapTicketToAPI, 
  TICKET_CATEGORIES_LIST, 
  TICKET_PRIORITIES_LIST 
} from "@/lib/translations";

interface TicketFormData {
  titulo: string;
  descricao: string;
  categoria: string;
  prioridade: string;
}

interface AttachedFile {
  file: File;
  id: string;
}

const ClientTicketNew = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<TicketFormData>({
    titulo: "",
    descricao: "",
    categoria: "",
    prioridade: "Média",
  });
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ ticketNumber: string } | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const handleInputChange = (
    field: keyof TicketFormData,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles: AttachedFile[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        errors.push(`${file.name} excede o tamanho máximo de 10MB`);
        return;
      }

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "image/png",
        "image/jpeg",
        "application/zip",
      ];

      if (!allowedTypes.includes(file.type)) {
        errors.push(`${file.name} não é um tipo de arquivo permitido`);
        return;
      }

      newFiles.push({
        file,
        id: `${Date.now()}-${Math.random()}`,
      });
    });

    if (errors.length > 0) {
      setError(errors.join(", "));
    } else {
      setError(null);
    }

    setAttachedFiles((prev) => [...prev, ...newFiles]);

    // Reset input
    e.target.value = "";
  };

  const removeFile = (id: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.titulo.trim()) {
      errors.titulo = "O título é obrigatório";
    }

    if (!formData.descricao.trim()) {
      errors.descricao = "A descrição é obrigatória";
    }

    if (!formData.categoria) {
      errors.categoria = "Selecione uma categoria";
    }

    if (!formData.prioridade) {
      errors.prioridade = "Selecione uma prioridade";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Converter dados do frontend (PT-BR) para API (EN) usando função centralizada
      const requestData = mapTicketToAPI(formData);

      const response = await authenticatedFetch("/api/tickets/index.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Erro ao criar ticket");
      }

      // A API retorna { message: "...", data: { id, ticket_number, ... } }
      const ticketData = data.data || data;
      
      // Upload de anexos se houver
      if (attachedFiles.length > 0) {
        try {
          const formData = new FormData();
          formData.append('ticket_id', ticketData.id.toString());
          
          // PHP espera files[] como array
          attachedFiles.forEach((attachedFile) => {
            formData.append('files[]', attachedFile.file);
          });
          
          console.log('Uploading attachments:', {
            ticket_id: ticketData.id,
            files_count: attachedFiles.length
          });

          const uploadResponse = await authenticatedFetch("/api/tickets/anexos.php", {
            method: "POST",
            body: formData,
          });

          if (!uploadResponse.ok) {
            console.error("Erro ao fazer upload de anexos");
            // Não bloqueia o fluxo, apenas loga o erro
          }
        } catch (uploadError) {
          console.error("Erro ao fazer upload de anexos:", uploadError);
          // Não bloqueia o fluxo
        }
      }
      
      // Show success message
      setSuccess({ ticketNumber: ticketData.ticket_number });

      // Redirect after 3 seconds
      setTimeout(() => {
        navigate(`/cliente/tickets/${ticketData.id}`);
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao criar ticket");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-2">Ticket criado com sucesso!</h2>
                <p className="text-muted-foreground">
                  Seu ticket <span className="font-mono font-semibold">#{success.ticketNumber}</span> foi criado.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Você receberá uma notificação por email e será redirecionado em instantes...
                </p>
              </div>
              <div className="flex gap-3">
                <Button asChild>
                  <Link to={`/cliente/tickets`}>Ver Meus Tickets</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/cliente/dashboard">Voltar ao Dashboard</Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <Button variant="ghost" asChild className="mb-4">
          <Link to="/cliente/tickets">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar para Tickets
          </Link>
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">Novo Ticket de Suporte</h1>
        <p className="text-muted-foreground mt-2">
          Preencha o formulário abaixo para abrir uma solicitação de suporte
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Informações do Ticket</CardTitle>
            <CardDescription>
              Forneça detalhes sobre sua solicitação para que possamos ajudá-lo melhor
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="titulo">
                Título <span className="text-destructive">*</span>
              </Label>
              <Input
                id="titulo"
                placeholder="Resumo breve do problema ou solicitação"
                value={formData.titulo}
                onChange={(e) => handleInputChange("titulo", e.target.value)}
                className={cn(validationErrors.titulo && "border-destructive")}
              />
              {validationErrors.titulo && (
                <p className="text-sm text-destructive">{validationErrors.titulo}</p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="descricao">
                Descrição <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="descricao"
                placeholder="Descreva detalhadamente sua solicitação ou problema..."
                value={formData.descricao}
                onChange={(e) => handleInputChange("descricao", e.target.value)}
                rows={6}
                className={cn(validationErrors.descricao && "border-destructive")}
              />
              {validationErrors.descricao && (
                <p className="text-sm text-destructive">{validationErrors.descricao}</p>
              )}
            </div>

            {/* Category and Priority */}
            <div className="grid gap-4 md:grid-cols-2">
              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="categoria">
                  Categoria <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.categoria}
                  onValueChange={(value) => handleInputChange("categoria", value)}
                >
                  <SelectTrigger
                    id="categoria"
                    className={cn(validationErrors.categoria && "border-destructive")}
                  >
                    <SelectValue placeholder="Selecione uma categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {TICKET_CATEGORIES_LIST.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {validationErrors.categoria && (
                  <p className="text-sm text-destructive">{validationErrors.categoria}</p>
                )}
              </div>

              {/* Priority */}
              <div className="space-y-2">
                <Label htmlFor="prioridade">
                  Prioridade <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.prioridade}
                  onValueChange={(value) => handleInputChange("prioridade", value)}
                >
                  <SelectTrigger
                    id="prioridade"
                    className={cn(validationErrors.prioridade && "border-destructive")}
                  >
                    <SelectValue placeholder="Selecione a prioridade" />
                  </SelectTrigger>
                  <SelectContent>
                    {TICKET_PRIORITIES_LIST.map((priority) => (
                      <SelectItem key={priority.value} value={priority.value}>
                        {priority.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {validationErrors.prioridade && (
                  <p className="text-sm text-destructive">{validationErrors.prioridade}</p>
                )}
              </div>
            </div>

            {/* File Attachments */}
            <div className="space-y-2">
              <Label>Anexos (Opcional)</Label>
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-2">
                  Arraste arquivos ou clique para selecionar
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, ZIP (máx. 10MB por arquivo)
                </p>
                <Input
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.zip"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById("file-upload")?.click()}
                >
                  Selecionar Arquivos
                </Button>
              </div>

              {/* Attached Files List */}
              {attachedFiles.length > 0 && (
                <div className="space-y-2 mt-4">
                  {attachedFiles.map((attachedFile) => (
                    <div
                      key={attachedFile.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {attachedFile.file.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(attachedFile.file.size)}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFile(attachedFile.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Submit Buttons */}
        <div className="flex justify-end gap-3 mt-6">
          <Button type="button" variant="outline" asChild disabled={isSubmitting}>
            <Link to="/cliente/tickets">Cancelar</Link>
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Criando..." : "Criar Ticket"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ClientTicketNew;
